import { useState, useCallback } from 'react';
import { message } from 'antd';
import { l204Api, FL140, L204CreateRequest, L204UpdateRequest } from '../../../services/l204Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useL204Mode';

/** Composite key for navigation */
export interface L204Key {
  docNo: string;
  pageNo: number;
}

export function useL204Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FL140 | null>(null);
  const [formData, setFormData] = useState<Partial<FL140>>({});
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by composite key */
  const loadRecord = useCallback(async (docNo: string, pageNo: number): Promise<FL140 | null> => {
    setLoading(true);
    try {
      const data = await l204Api.get(docNo, pageNo);
      setRecord(data);
      setFormData({ ...data });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FL140, value: FL140[keyof FL140]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FL140): FL140[keyof FL140] => {
    return (formData as Record<string, FL140[keyof FL140]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FL140) => void,
  ) => {
    setLoading(true);
    try {
      if (mode === 'add') {
        const req: L204CreateRequest = {
          fl140_01: (formData.fl140_01 as string) || '',
          fl140_02: (formData.fl140_02 as number) || 0,
          fl140_11: (formData.fl140_11 as string) || '',
          fl140_12: (formData.fl140_12 as string) || '',
          fl140_13: (formData.fl140_13 as string) || '',
          fl140_21: (formData.fl140_21 as string) || '',
        };
        const result = await l204Api.create(req);
        message.success(`新增成功: ${result.fl140_01} 頁${result.fl140_02}`);
        onSuccess(result);
      } else {
        const req: L204UpdateRequest = {
          fl140_11: (formData.fl140_11 as string) || '',
          fl140_12: (formData.fl140_12 as string) || '',
          fl140_13: (formData.fl140_13 as string) || '',
          fl140_21: (formData.fl140_21 as string) || '',
        };
        const docNo = formData.fl140_01 as string;
        const pageNo = formData.fl140_02 as number;
        const result = await l204Api.update(docNo, pageNo, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await l204Api.delete(record.fl140_01, record.fl140_02);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fl140_01: '',
      fl140_02: 1,
      fl140_11: '',
      fl140_12: '',
      fl140_13: '',
      fl140_21: '',
      fl140_91: user?.userId || '',
    });
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FL140) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors, hasErrors,
  };
}
